import { FC, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import DocumentGreen from 'src/assets/images/icons/document-text-green.svg';
import DocumentAccept from 'src/assets/images/icons/document-sketch.svg';
import CloseCircle from 'src/assets/images/icons/close-circle.svg';

import { useNrcfOptions } from 'src/use-cases/invoice/useNoContract';
import { CircularProgress } from '@mui/material';
import { NoContractForm, NoContractOption } from './types';
import { useShipmentSelector } from '../ShipmentSelector';

interface INoContractContentStep1Props {
  onSuccessSubmit?: () => void;
  billedEntityId?: number;
}

interface VerdictOption {
  value: NoContractOption;
  icon: string;
  iconAlt: string;
  title: string;
  description: string;
  id: string; // Maps to API response values
}

const allVerdictOptions: VerdictOption[] = [
  {
    value: 'setup-new',
    icon: DocumentGreen,
    iconAlt: 'setup new service icon',
    title: 'Setup a New Service',
    description: 'copy or setup a new service in rate card',
    id: 'SET_UP_NEW_SERVICE',
  },
  {
    value: 'accept',
    icon: DocumentAccept,
    iconAlt: 'accept as spot shipment icon',
    title: 'Accept as Spot Shipment',
    description: 'proceed without setting up a new rate card',
    id: 'ACCEPT_AS_SPOT_SHIPMENT',
  },
  {
    value: 'reject',
    icon: CloseCircle,
    iconAlt: 'reject shipment icon',
    title: 'Reject Shipment',
    description: "reject all the shipment's charges",
    id: 'REJECT_SHIPMENT',
  },
];

export const NoContractContentStep1: FC<INoContractContentStep1Props> = ({ onSuccessSubmit, billedEntityId }) => {
  const { watch, setValue } = useFormContext<NoContractForm>();
  const { verdict } = watch();
  const { shipment } = useShipmentSelector();

  // Fetch NRCF options for the billed entity
  const { data: nrcfOptions = [], isLoading } = useNrcfOptions(billedEntityId);

  // Determine workflow type
  const isThresholdWorkflow = useMemo(
    () => ['SUPPLY_CHAIN_REVIEW', 'FINANCE_REVIEW'].includes(shipment?.currentDeviationWorkflow || ''),
    [shipment?.currentDeviationWorkflow]
  );

  // Filter verdict options based on workflow type AND NRCF options
  const availableVerdictOptions = useMemo(() => {
    let filteredOptions = allVerdictOptions;

    // First: Filter by workflow type
    if (isThresholdWorkflow) {
      // Threshold workflow: Only accept and reject (no setup-new)
      filteredOptions = filteredOptions.filter((option) => option.value === 'accept' || option.value === 'reject');
    }

    // Second: Filter by NRCF options (if provided)
    if (nrcfOptions && nrcfOptions.length > 0) {
      filteredOptions = filteredOptions.filter((option) => nrcfOptions.includes(option.id));
    }

    return filteredOptions;
  }, [isThresholdWorkflow, nrcfOptions]);

  const handleSelectVerdict = (value: NoContractOption) => {
    setValue('verdict', value);

    // Automatically proceed to the next step for all options
    if (onSuccessSubmit) {
      onSuccessSubmit();
    }
  };

  return !billedEntityId || isLoading ? (
    <div className='p-32 xl:p-48 flex items-center justify-center'>
      <CircularProgress color='primary' />
    </div>
  ) : (
    <div className='px-24 pt-12 pb-24'>
      <div className='mb-20'>
        <h2 className='text-16 xl:text-20 leading-24 xl:leading-[32px] font-bold'>Select an Option</h2>
        <p className='text-[#475B69] text-14 xl:text-16 leading-[170%] font-normal'>
          {isThresholdWorkflow ? 'Please choose how to handle this threshold review:' : 'Please choose one:'}
        </p>
      </div>

      <div className='flex flex-col md:flex-row gap-16'>
        {availableVerdictOptions.map((option) => {
          const isSelected = verdict === option.value;
          const isReject = option.value === 'reject';

          const getBorderClasses = () => {
            if (isReject) {
              return isSelected ? 'border-[#E72547] shadow-md bg-white' : 'border-[#DFDFDF] bg-white hover:border-[#E72547]/30';
            }
            return isSelected ? 'border-[#00992B] shadow-md bg-white' : 'border-[#DFDFDF] bg-white hover:border-[#00992B]/30';
          };

          return (
            <button
              key={option.value}
              type='button'
              onClick={() => handleSelectVerdict(option.value)}
              className={`
                flex-1 p-20 rounded-20 border-2 transition-all
                ${getBorderClasses()}
              `}
              disabled={isLoading}
            >
              <div className='flex items-start gap-12 h-full'>
                <div className='flex-shrink-0 flex items-center justify-center h-full'>
                  <img src={option.icon} alt={option.iconAlt} className='w-36' />
                </div>
                <div className='flex-1 text-left'>
                  <h3 className='text-16 xl:text-18 font-bold mb-4'>{option.title}</h3>
                  <p className='text-12 xl:text-14 text-[#808080]'>{option.description}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
